import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2pdf from 'html2pdf.js';
import { Download, Loader2 } from 'lucide-react';
export function LetterSizePreview({ content, documentTitle = 'Document', showDownloadButton = true }) {
    const contentRef = useRef(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [pages, setPages] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    // Split content into pages based on estimated length
    useEffect(() => {
        // For now, render all content on first page and calculate pages after render
        setPages([{ content, pageNumber: 1 }]);
    }, [content]);
    // Calculate total pages after content is rendered
    useEffect(() => {
        if (contentRef.current) {
            const contentHeight = contentRef.current.scrollHeight;
            // Letter size content area: 11in - 1.5in (margins) = 9.5in
            // At 96 DPI: 9.5in = 912px
            const pageContentHeight = 912;
            const calculatedPages = Math.ceil(contentHeight / pageContentHeight);
            setTotalPages(Math.max(1, calculatedPages));
        }
    }, [content]);
    const handleDownloadPDF = async () => {
        if (!contentRef.current)
            return;
        setIsGenerating(true);
        try {
            const element = contentRef.current;
            const opt = {
                margin: [0.75, 0.75, 0.75, 0.75],
                filename: `${documentTitle.replace(/\s+/g, '_')}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    letterRendering: true,
                    scrollY: 0,
                    scrollX: 0
                },
                jsPDF: {
                    unit: 'in',
                    format: 'letter',
                    orientation: 'portrait',
                    compress: true
                },
                pagebreak: {
                    mode: ['avoid-all', 'css', 'legacy'],
                    before: '.page-break-before',
                    after: '.page-break-after',
                    avoid: ['tr', 'td', 'th', 'img', 'table', '.no-break']
                }
            };
            await html2pdf().set(opt).from(element).save();
            console.log('[LetterSizePreview] PDF generated successfully');
        }
        catch (error) {
            console.error('[LetterSizePreview] PDF generation error:', error);
            alert('Failed to generate PDF. Please try again.');
        }
        finally {
            setIsGenerating(false);
        }
    };
    return (_jsxs("div", { className: "relative", children: [showDownloadButton && (_jsx("div", { className: "flex justify-end mb-6", children: _jsx("button", { onClick: handleDownloadPDF, disabled: isGenerating, className: "flex items-center gap-2 px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg", style: {
                        backgroundColor: isGenerating ? '#9ca3af' : '#D97706',
                        color: 'white'
                    }, children: isGenerating ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { className: "w-5 h-5 animate-spin" }), "Generating PDF..."] })) : (_jsxs(_Fragment, { children: [_jsx(Download, { className: "w-5 h-5" }), "Download as PDF"] })) }) })), _jsxs("div", { className: "print-preview-container", style: {
                    backgroundColor: '#525659',
                    padding: '40px 20px',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '20px'
                }, children: [_jsx("style", { children: `
          /* Print Preview Styles */
          .print-preview-page {
            width: 8.5in;
            height: 11in;
            backgroundColor: white;
            boxShadow: 0 0 0 1px rgba(0, 0, 0, 0.1),
                        0 4px 6px rgba(0, 0, 0, 0.1),
                        0 10px 20px rgba(0, 0, 0, 0.15);
            position: relative;
            overflow: hidden;
            margin-bottom: 20px;
          }

          .print-preview-page:last-child {
            margin-bottom: 0;
          }

          /* Letter Size Document Styles */
          .letter-size-content {
            font-family: 'Times New Roman', 'Georgia', serif;
            font-size: 12pt;
            line-height: 1.6;
            color: #000000;
            padding: 0.75in;
            height: 100%;
            box-sizing: border-box;
          }

          /* Headings */
          .letter-size-content h1 {
            font-family: 'Times New Roman', 'Georgia', serif;
            font-size: 18pt;
            font-weight: bold;
            line-height: 1.3;
            margin: 0 0 12pt 0;
            color: #000000;
            text-align: center;
            page-break-after: avoid;
          }

          .letter-size-content h1:first-child {
            margin-top: 0;
          }

          .letter-size-content h2 {
            font-family: 'Times New Roman', 'Georgia', serif;
            font-size: 16pt;
            font-weight: bold;
            line-height: 1.3;
            margin: 18pt 0 10pt 0;
            color: #000000;
            page-break-after: avoid;
          }

          .letter-size-content h3 {
            font-family: 'Times New Roman', 'Georgia', serif;
            font-size: 14pt;
            font-weight: bold;
            line-height: 1.3;
            margin: 14pt 0 8pt 0;
            color: #000000;
            page-break-after: avoid;
          }

          .letter-size-content h4 {
            font-family: 'Times New Roman', 'Georgia', serif;
            font-size: 13pt;
            font-weight: bold;
            line-height: 1.3;
            margin: 12pt 0 6pt 0;
            color: #000000;
            page-break-after: avoid;
          }

          .letter-size-content h5 {
            font-family: 'Times New Roman', 'Georgia', serif;
            font-size: 12pt;
            font-weight: bold;
            line-height: 1.3;
            margin: 10pt 0 6pt 0;
            color: #000000;
            page-break-after: avoid;
          }

          .letter-size-content h6 {
            font-family: 'Times New Roman', 'Georgia', serif;
            font-size: 12pt;
            font-weight: bold;
            font-style: italic;
            line-height: 1.3;
            margin: 8pt 0 4pt 0;
            color: #000000;
            page-break-after: avoid;
          }

          /* Paragraphs */
          .letter-size-content p {
            margin: 0 0 12pt 0;
            text-align: justify;
            text-justify: inter-word;
            orphans: 3;
            widows: 3;
          }

          /* Strong and Emphasis */
          .letter-size-content strong {
            font-weight: bold;
          }

          .letter-size-content em {
            font-style: italic;
          }

          /* Lists */
          .letter-size-content ul,
          .letter-size-content ol {
            margin: 0 0 12pt 0;
            padding-left: 0.5in;
          }

          .letter-size-content li {
            margin-bottom: 6pt;
            page-break-inside: avoid;
          }

          .letter-size-content ul {
            list-style-type: disc;
          }

          .letter-size-content ol {
            list-style-type: decimal;
          }

          .letter-size-content ul ul {
            list-style-type: circle;
            margin-bottom: 0;
          }

          .letter-size-content ol ol {
            list-style-type: lower-alpha;
            margin-bottom: 0;
          }

          /* Blockquotes */
          .letter-size-content blockquote {
            margin: 12pt 0.5in 12pt 0.5in;
            padding: 8pt 12pt;
            border-left: 3pt solid #d97706;
            background-color: #fffbeb;
            font-style: italic;
            page-break-inside: avoid;
          }

          /* Code */
          .letter-size-content code {
            font-family: 'Courier New', monospace;
            font-size: 11pt;
            background-color: #f3f4f6;
            padding: 2pt 4pt;
            border-radius: 2pt;
          }

          .letter-size-content pre {
            background-color: #f3f4f6;
            padding: 12pt;
            border-radius: 4pt;
            overflow-x: auto;
            margin: 12pt 0;
            page-break-inside: avoid;
          }

          .letter-size-content pre code {
            background-color: transparent;
            padding: 0;
          }

          /* Horizontal Rules */
          .letter-size-content hr {
            margin: 18pt 0;
            border: none;
            border-top: 1pt solid #000000;
            page-break-after: avoid;
          }

          /* Tables */
          .letter-size-content table {
            width: 100%;
            border-collapse: collapse;
            margin: 12pt 0;
            font-size: 11pt;
            page-break-inside: avoid;
            border: 1pt solid #000000;
          }

          .letter-size-content table.no-break {
            page-break-inside: avoid;
          }

          .letter-size-content th {
            background-color: #f3f4f6;
            font-weight: bold;
            text-align: left;
            padding: 8pt;
            border: 1pt solid #000000;
            page-break-after: avoid;
          }

          .letter-size-content td {
            padding: 8pt;
            border: 1pt solid #000000;
            vertical-align: top;
          }

          .letter-size-content tbody tr {
            page-break-inside: avoid;
          }

          /* Links */
          .letter-size-content a {
            color: #1a56db;
            text-decoration: underline;
          }

          /* Images */
          .letter-size-content img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 12pt auto;
            page-break-inside: avoid;
          }

          /* Page Break Utilities */
          .page-break-before {
            page-break-before: always;
          }

          .page-break-after {
            page-break-after: always;
          }

          .no-break {
            page-break-inside: avoid;
          }

          /* Page Number Footer */
          .page-number-footer {
            position: absolute;
            bottom: 0.4in;
            left: 0;
            right: 0;
            text-align: center;
            font-family: 'Times New Roman', 'Georgia', serif;
            font-size: 10pt;
            color: #666;
            pointer-events: none;
          }

          /* Visual Page Separators (every 11 inches of content) */
          .page-separator {
            position: absolute;
            width: 100%;
            height: 1px;
            background: linear-gradient(90deg, transparent, rgba(0,0,0,0.1) 20%, rgba(0,0,0,0.1) 80%, transparent);
            pointer-events: none;
            z-index: 1;
          }

          /* Print-specific styles */
          @media print {
            .print-preview-container {
              background: white;
              padding: 0;
            }

            .print-preview-page {
              box-shadow: none;
              margin: 0;
              page-break-after: always;
            }

            .print-preview-page:last-child {
              page-break-after: auto;
            }

            .page-number-footer {
              display: block;
            }

            @page {
              size: letter;
              margin: 0.75in;
            }
          }
        ` }), _jsxs("div", { className: "print-preview-page", children: [_jsx("div", { ref: contentRef, className: "letter-size-content", children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: {
                                        h1: ({ node, ...props }) => _jsx("h1", { className: "no-break", ...props }),
                                        h2: ({ node, ...props }) => _jsx("h2", { className: "no-break", ...props }),
                                        h3: ({ node, ...props }) => _jsx("h3", { className: "no-break", ...props }),
                                        h4: ({ node, ...props }) => _jsx("h4", { className: "no-break", ...props }),
                                        h5: ({ node, ...props }) => _jsx("h5", { className: "no-break", ...props }),
                                        h6: ({ node, ...props }) => _jsx("h6", { className: "no-break", ...props }),
                                        p: ({ node, ...props }) => _jsx("p", { ...props }),
                                        a: ({ node, ...props }) => _jsx("a", { ...props, target: "_blank", rel: "noopener noreferrer" }),
                                        ul: ({ node, ...props }) => _jsx("ul", { ...props }),
                                        ol: ({ node, ...props }) => _jsx("ol", { ...props }),
                                        li: ({ node, ...props }) => _jsx("li", { className: "no-break", ...props }),
                                        blockquote: ({ node, ...props }) => _jsx("blockquote", { className: "no-break", ...props }),
                                        code: ({ node, inline, ...props }) => inline ? _jsx("code", { ...props }) : _jsx("pre", { className: "no-break", children: _jsx("code", { ...props }) }),
                                        hr: ({ node, ...props }) => _jsx("hr", { ...props }),
                                        table: ({ node, ...props }) => _jsx("table", { className: "no-break", ...props }),
                                        thead: ({ node, ...props }) => _jsx("thead", { ...props }),
                                        tbody: ({ node, ...props }) => _jsx("tbody", { ...props }),
                                        tr: ({ node, ...props }) => _jsx("tr", { className: "no-break", ...props }),
                                        th: ({ node, ...props }) => _jsx("th", { ...props }),
                                        td: ({ node, ...props }) => _jsx("td", { ...props }),
                                        img: ({ node, ...props }) => _jsx("img", { className: "no-break", ...props, alt: props.alt || "Document image" }),
                                        strong: ({ node, ...props }) => _jsx("strong", { ...props }),
                                        em: ({ node, ...props }) => _jsx("em", { ...props }),
                                    }, children: content }) }), _jsxs("div", { className: "page-number-footer", children: ["Page 1 ", totalPages > 1 ? `of ${totalPages}` : ''] }), totalPages > 1 && Array.from({ length: totalPages - 1 }, (_, i) => (_jsx("div", { className: "page-separator", style: {
                                    top: `${(i + 1) * 9.5}in`
                                } }, `separator-${i}`)))] }), totalPages > 1 && Array.from({ length: totalPages - 1 }, (_, i) => (_jsxs("div", { className: "print-preview-page", children: [_jsx("div", { className: "letter-size-content", children: _jsxs("div", { style: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        color: '#999',
                                        fontSize: '14pt'
                                    }, children: ["(Content continues from page ", i + 1, ")"] }) }), _jsxs("div", { className: "page-number-footer", children: ["Page ", i + 2, " of ", totalPages] })] }, `page-${i + 2}`)))] })] }));
}
